import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { applyTypography } from '../../styled/theme/typography-utils';
import LytIcon from '../../library/icon/LytIcon';
import { ICON_NAMES } from '../../library/icon/icon-constants';
const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: ${({ theme }) =>
        theme?.components?.lyt_modal?.overlay?.background || 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;
const Dialog = styled.div`
    background: ${({ theme }) =>
        theme?.components?.lyt_modal?.dialog?.background ||
        'var(--elevation-surfaces-primary, #FFF)'};
    color: ${({ theme }) =>
        theme?.components?.lyt_modal?.dialog?.text || '#000'};
    border: 1px solid
        ${({ theme }) =>
            theme?.components?.lyt_modal?.dialog?.border || 'transparent'};
    border-radius: 4px;
    box-shadow: ${({ theme }) =>
        theme?.components?.lyt_modal?.dialog?.shadow ||
        '0 1px 4.7px 0 rgba(31, 31, 31, 0.29)'};
    width: ${({ size }) => {
        switch (size) {
            case 'small':
                return '400px';
            case 'large':
                return '800px';
            case 'medium':
            default:
                return '600px';
        }
    }};
    max-width: 90vw;
    max-height: 800px;
    outline: none;
    display: flex;
    flex-direction: column;
    overflow: hidden; /* Prevent dialog itself from scrolling */
`;
const Header = styled.div`
    padding: 24px 24px 8px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0; /* Header stays fixed */
`;
const HeaderTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
const HeaderTitle = styled.h2`
    ${applyTypography('3_bold')}
    color: ${({ theme }) =>
        theme?.components?.lyt_modal?.header?.text ||
        theme?.text?.default ||
        '#000'};
    background: ${({ theme }) =>
        theme?.components?.lyt_modal?.header?.background || 'transparent'};
    margin: 0;
`;
const Body = styled.div`
    padding: 0 var(--space-4, 24px) var(--space-4, 24px) var(--space-4, 24px);
    color: ${({ theme }) =>
        theme?.components?.lyt_modal?.body?.text || 'inherit'};
    background: ${({ theme }) =>
        theme?.components?.lyt_modal?.body?.background || 'transparent'};
    flex: 1; /* Takes remaining space */
    overflow-y: auto; /* Only body content scrolls */
    min-height: 0; /* Allow flex item to shrink below content size */
`;
export const LytModalFooter = styled.div`
    padding: 24px;
    background: ${({ theme }) =>
        theme?.components?.lyt_modal?.footer?.background || 'transparent'};
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    flex-shrink: 0; /* Footer stays fixed */
`;
const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) =>
        theme?.components?.lyt_modal?.closeButton?.background || 'transparent'};
    color: ${({ theme }) =>
        theme?.components?.lyt_modal?.closeButton?.text ||
        theme?.text?.subtle ||
        '#474749'};
    border: none;
    cursor: pointer;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ theme }) =>
            theme?.components?.lyt_modal?.closeButton?.hover?.background ||
            'rgba(0, 0, 0, 0.05)'};
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme?.colors?.Blue1 || '#0066cc'};
        outline-offset: 2px;
    }
`;

/**
 * Accessible modal primitive with portal + basic focus trap
 */
const LytModal = ({
    open = false,
    title = undefined,
    titleIcon = null,
    titleBadge = null,
    onClose = undefined,
    children = null,
    ariaLabelledBy = undefined,
    ariaDescribedBy = undefined,
    closeOnOverlay = true,
    actions = null,
    size = 'medium',
}) => {
    const dialogRef = useRef(null);
    const lastFocusedRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        try {
            lastFocusedRef.current = document.activeElement;
        } catch (_) {
            lastFocusedRef.current = null;
        }
        // Move focus into dialog
        const dialogEl = dialogRef.current;
        if (dialogEl && typeof dialogEl.focus === 'function') {
            dialogEl.focus();
        }
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose?.({
                    reason: 'escape',
                });
            }
            if (e.key === 'Tab') {
                // very basic focus trap
                const focusable = dialogEl?.querySelectorAll(
                    'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
                );
                if (!focusable || focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            try {
                const el = lastFocusedRef.current;
                if (el && typeof el.focus === 'function') el.focus();
            } catch (err) {
                // ignore focus restore errors
            }
        };
    }, [open, onClose]);
    if (!open) return null;
    const content = (
        <Overlay
            onMouseDown={(e) => {
                if (!closeOnOverlay) return;
                // Only close if clicking the overlay, not the dialog
                if (e.target === e.currentTarget) {
                    onClose?.({
                        reason: 'overlay',
                    });
                }
            }}
            aria-hidden={false}>
            <Dialog
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                tabIndex={-1}
                size={size}>
                <Header>
                    <HeaderTitleContainer>
                        {titleIcon}
                        {title ? (
                            <HeaderTitle id={ariaLabelledBy}>
                                {title}
                            </HeaderTitle>
                        ) : null}
                        {titleBadge}
                    </HeaderTitleContainer>
                    <CloseButton
                        aria-label="Close"
                        onClick={() =>
                            onClose?.({
                                reason: 'close-button',
                            })
                        }>
                        <LytIcon name={ICON_NAMES.X} />
                    </CloseButton>
                </Header>
                <Body>{children}</Body>
                {actions && <LytModalFooter>{actions}</LytModalFooter>}
            </Dialog>
        </Overlay>
    );
    const portalTarget =
        typeof document !== 'undefined'
            ? document.getElementById('modal-root') || document.body
            : null;
    return portalTarget
        ? ReactDOM.createPortal(content, portalTarget)
        : content;
};
LytModal.propTypes = {
    open: PropTypes.bool,
    title: PropTypes.string,
    titleIcon: PropTypes.node,
    titleBadge: PropTypes.node,
    // Badge/element rendered after the title
    onClose: PropTypes.func,
    children: PropTypes.node,
    ariaLabelledBy: PropTypes.string,
    ariaDescribedBy: PropTypes.string,
    closeOnOverlay: PropTypes.bool,
    actions: PropTypes.node,
    // JSX element with buttons
    size: PropTypes.oneOf(['small', 'medium', 'large']),
};
export default LytModal;
